import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { FiMoreHorizontal, FiPlus, FiStar, FiEdit2, FiSmartphone, FiTrash2, FiLink, FiLink2 } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { fetchAssistants } from '../store/slices/assistantSlice';
import type { Assistant } from '../store/slices/assistantSlice';
import api from '../utils/api';

type WidgetType = 'Floating Orb' | 'Programmable Button';

type Widget = {
  id: string;
  name: string;
  type: WidgetType;
  createdAt: string;
  updatedAt: string;
  linkedAssistant?: string;
  favorited?: boolean;
  archived?: boolean;
  settings?: {
    theme?: 'light' | 'dark' | 'auto';
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    customCss?: string;
    initialMessage?: string;
    buttonText?: string;
    iconType?: 'default' | 'custom';
    iconUrl?: string;
  };
};

const LOCAL_KEY = 'looplet.widgets.v1';

const defaultWidgets: Widget[] = [
  {
    id: 'demo-floating-orb',
    name: 'Morphic Website Demo Booking...',
    type: 'Floating Orb',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    linkedAssistant: '*DON\'T TOUCH* - Morphic Demo',
    favorited: false,
  },
  {
    id: 'demo-programmable-button',
    name: 'dr nimrod',
    type: 'Programmable Button',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 20).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 20).toISOString(),
    favorited: false,
  },
];

const loadWidgets = async (): Promise<Widget[]> => {
	try {
		const resp = await api.get('/widgets');
		if (Array.isArray(resp.data?.widgets)) return resp.data.widgets as Widget[];
	} catch {}
	try {
		const raw = localStorage.getItem(LOCAL_KEY);
		if (!raw) return defaultWidgets;
		const parsed = JSON.parse(raw);
		if (Array.isArray(parsed)) return parsed as Widget[];
		return defaultWidgets;
	} catch {
		return defaultWidgets;
	}
};

const saveWidgets = async (widgets: Widget[], assistantIdForCreate?: string) => {
	try {
		// Persist only changed or new widgets in a simple upsert loop (best-effort)
		await Promise.all(widgets.map(async (w) => {
			try {
				await api.put(`/widgets/${w.id}`, { config: w.settings, name: w.name, assistantId: assistantIdForCreate || undefined });
			} catch {
				try {
					if (!assistantIdForCreate) throw new Error('assistantId required');
					await api.post('/widgets', { assistantId: assistantIdForCreate, name: w.name, config: w.settings });
				} catch {}
			}
		}));
	} catch {}
	try {
		localStorage.setItem(LOCAL_KEY, JSON.stringify(widgets));
	} catch {}
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.5)'
};

const cardStyle = {
  background: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border-primary)'
};

const generateEmbedCode = (widget: Widget) => {
  const baseUrl = window.location.origin;
  const scriptUrl = `${baseUrl}/api/widgets/${widget.id}/embed.js`;
  return `<script src="${scriptUrl}" defer></script>`;
};

const generateTestUrl = (widget: Widget) => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/api/widgets/${widget.id}/test`;
};

const Widgets: React.FC = () => {
  const { themeMode } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState<string | null>(null);
  const [newWidgetName, setNewWidgetName] = useState('');
  const [newWidgetType, setNewWidgetType] = useState<WidgetType>('Floating Orb');
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrWidget, setQrWidget] = useState<Widget | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>('');
  const [editSettings, setEditSettings] = useState<Widget['settings']>({
    theme: 'auto',
    position: 'bottom-right',
    initialMessage: '',
    buttonText: '',
    iconType: 'default'
  });

  // Load assistants on mount
  useEffect(() => {
    dispatch(fetchAssistants())
      .unwrap()
      .then(data => {
        setAssistants(data);
      })
      .catch(console.error);
  }, [dispatch]);

  // Save widgets when they change
  useEffect(() => {
    (async () => { await saveWidgets(widgets, selectedAssistantId); })();
  }, [widgets, selectedAssistantId]);

  const stats = useMemo(() => ({
    all: widgets.filter(w => !w.archived).length,
    favorites: widgets.filter(w => w.favorited && !w.archived).length,
    archived: widgets.filter(w => w.archived).length,
  }), [widgets]);

  const createWidget = () => {
    if (!newWidgetName.trim()) return;
    if (!selectedAssistantId) return;
    
    // Find selected assistant details
    const selectedAssistant = assistants.find(a => a.id === selectedAssistantId);
    
    const now = new Date().toISOString();
    const w: Widget = {
      id: `w_${Math.random().toString(36).slice(2)}`,
      name: newWidgetName.trim(),
      type: newWidgetType,
      createdAt: now,
      updatedAt: now,
      favorited: false,
      linkedAssistant: selectedAssistant?.name || '',
      settings: {
        theme: 'auto',
        position: 'bottom-right',
        initialMessage: `Hi! I'm ${selectedAssistant?.name || 'your assistant'}. How can I help you today?`,
        buttonText: 'Chat with us',
        iconType: 'default'
      }
    };
    setWidgets(prev => [w, ...prev]);
    setShowCreate(false);
    setNewWidgetName('');
    setSelectedAssistantId('');
  };

  const deleteWidget = async (id: string) => {
    try { await api.delete(`/widgets/${id}`); } catch {}
    setWidgets(prev => prev.filter(w => w.id !== id));
  };

  const toggleFavorite = async (id: string) => {
    setWidgets(prev => prev.map(w => w.id === id ? { ...w, favorited: !w.favorited } : w));
    const w = widgets.find(x => x.id === id);
    if (w) {
      const newCfg = { ...(w.settings || {}), favorited: !w.favorited } as any;
      try { await api.put(`/widgets/${id}`, { config: newCfg, name: w.name }); } catch {}
    }
  };

  const testOnMobile = (widget: Widget) => {
    const testUrl = generateTestUrl(widget);
    const img = `https://api.qrserver.com/v1/create-qr-code/?size=380x380&data=${encodeURIComponent(testUrl)}`;
    setQrWidget(widget);
    setQrUrl(img);
  };

  const startEdit = (widget: Widget) => {
    setShowEdit(widget.id);
    setEditSettings(widget.settings || {
      theme: 'auto',
      position: 'bottom-right',
      initialMessage: '',
      buttonText: '',
      iconType: 'default'
    });
  };

  const saveEdit = async (id: string) => {
    const target = widgets.find(w => w.id === id);
    const linkedAssistantName = assistants.find(a => a.id === selectedAssistantId)?.name || target?.linkedAssistant || '';
    setWidgets(prev => prev.map(w => w.id === id ? { ...w, settings: editSettings, linkedAssistant: linkedAssistantName, updatedAt: new Date().toISOString() } : w));
    try { await api.put(`/widgets/${id}`, { config: editSettings, name: target?.name, assistantId: selectedAssistantId || undefined }); } catch {}
    setShowEdit(null);
  };

  return (
    <div className="p-6" data-theme={themeMode}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>Widgets</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-500"
          style={{ color: 'var(--color-text-primary)' }}
        >
          <FiPlus />
          Create Widget
        </button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-lg" style={cardStyle}>
          <div className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>All</div>
          <div className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stats.all}</div>
        </div>
        <div className="p-4 rounded-lg" style={cardStyle}>
          <div className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Favorites</div>
          <div className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stats.favorites}</div>
        </div>
        <div className="p-4 rounded-lg" style={cardStyle}>
          <div className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Archived</div>
          <div className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stats.archived}</div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg overflow-visible" style={cardStyle}>
        <div className="grid grid-cols-12 px-4 py-3 text-sm font-medium border-b" 
             style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', background: 'rgba(0,0,0,0.2)' }}>
          <div className="col-span-4 pl-2">NAME</div>
          <div className="col-span-2 text-center">UPDATED</div>
          <div className="col-span-2 text-center">CREATED</div>
          <div className="col-span-2 text-center">TYPE</div>
          <div className="col-span-2 text-right pr-12">LINKED ASSISTANT</div>
        </div>
        {widgets.length === 0 ? (
          <div className="p-6 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>No widgets yet. Create one to get started.</div>
        ) : widgets.map(w => (
          <div key={w.id} className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b last:border-b-0 hover:bg-gray-800/30 transition-colors" 
               style={{ borderColor: 'var(--color-border-primary)' }}>
            <div className="col-span-4 flex items-center gap-2 pl-2">
              <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{w.name}</span>
            </div>
            <div className="col-span-2 text-center text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {new Date(w.updatedAt).toLocaleDateString()} 
              <br />
              <span className="opacity-60">{new Date(w.updatedAt).toLocaleTimeString()}</span>
            </div>
            <div className="col-span-2 text-center text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {new Date(w.createdAt).toLocaleDateString()}
              <br />
              <span className="opacity-60">{new Date(w.createdAt).toLocaleTimeString()}</span>
            </div>
            <div className="col-span-2 text-center">
              <span className="px-2 py-1 rounded text-xs font-medium" 
                    style={{ 
                      background: w.type === 'Floating Orb' ? 'rgba(147, 51, 234, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                      color: w.type === 'Floating Orb' ? '#c084fc' : '#93c5fd'
                    }}>
                {w.type}
              </span>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2 pr-2">
              {w.linkedAssistant ? (
                <span className="text-xs font-medium px-2 py-1 rounded bg-green-500/20 text-green-400">
                  {w.linkedAssistant}
                </span>
              ) : (
                <span className="text-xs px-2 py-1 rounded bg-gray-700/50" style={{ color: 'var(--color-text-secondary)' }}>
                  No assigned assistant
                </span>
              )}
              <div className="relative inline-block text-left">
                <button
                  onClick={() => setMenuOpenId(menuOpenId === w.id ? null : w.id)}
                  className="p-1 rounded hover:bg-gray-700/30"
                  title="Actions"
                  aria-haspopup="menu"
                >
                  <FiMoreHorizontal />
                </button>
                {menuOpenId === w.id && (
                  <div className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg shadow-xl z-20 focus:outline-none" style={{ ...cardStyle, overflow: 'hidden' }} role="menu">
                    <button
                      onClick={() => { toggleFavorite(w.id); setMenuOpenId(null); }}
                      className="w-full flex items-center gap-2 px-4 py-2 whitespace-nowrap hover:bg-gray-100/5"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiStar className={w.favorited ? 'text-yellow-400' : ''} />
                      {w.favorited ? 'Remove from favorites' : 'Add to favorites'}
                    </button>
                    <button
                      onClick={() => { testOnMobile(w); setMenuOpenId(null); }}
                      className="w-full flex items-center gap-2 px-4 py-2 whitespace-nowrap hover:bg-gray-100/5"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiSmartphone />
                      Test on mobile
                    </button>
                    <button
                      onClick={() => { startEdit(w); setMenuOpenId(null); }}
                      className="w-full flex items-center gap-2 px-4 py-2 whitespace-nowrap hover:bg-gray-100/5"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      <FiEdit2 />
                      Edit widget
                    </button>
                    <div className="my-1" style={{ borderTop: '1px solid var(--color-border-primary)' }} />
                    <button
                      onClick={() => { deleteWidget(w.id); setMenuOpenId(null); }}
                      className="w-full flex items-center gap-2 px-4 py-2 whitespace-nowrap text-red-500 hover:bg-red-500/10"
                    >
                      <FiTrash2 />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div style={modalBackdropStyle} role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-lg p-5" style={cardStyle}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Create Widget</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Name</label>
                <input
                  value={newWidgetName}
                  onChange={e => setNewWidgetName(e.target.value)}
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Type</label>
                <select
                  value={newWidgetType}
                  onChange={e => setNewWidgetType(e.target.value as WidgetType)}
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <option>Floating Orb</option>
                  <option>Programmable Button</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Link Assistant
                  <span className="text-xs ml-1" style={{ color: 'var(--color-text-secondary)' }}>
                    (required)
                  </span>
                </label>
                <select
                  value={selectedAssistantId}
                  onChange={e => setSelectedAssistantId(e.target.value)}
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <option value="">Select an assistant...</option>
                  {assistants.map(assistant => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded bg-gray-700/50 hover:bg-gray-700 text-sm">Cancel</button>
              <button onClick={createWidget} className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 text-sm">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrUrl && qrWidget && (
        <div style={modalBackdropStyle} role="dialog" aria-modal="true" onClick={() => { setQrUrl(null); setQrWidget(null); }}>
          <div className="w-full max-w-xl rounded-lg p-5" style={cardStyle} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Test Widget on Mobile</h3>
            <div className="mb-4 p-4 rounded" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <div className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>Widget Details</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div style={{ color: 'var(--color-text-secondary)' }}>Name:</div>
                <div style={{ color: 'var(--color-text-primary)' }}>{qrWidget.name}</div>
                <div style={{ color: 'var(--color-text-secondary)' }}>Type:</div>
                <div style={{ color: 'var(--color-text-primary)' }}>{qrWidget.type}</div>
                <div style={{ color: 'var(--color-text-secondary)' }}>Assistant:</div>
                <div style={{ color: 'var(--color-text-primary)' }}>{qrWidget.linkedAssistant || 'None'}</div>
              </div>
            </div>
            <img src={qrUrl} alt="QR code" className="mx-auto mb-4" />
            <div className="text-center mb-4">
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>Scan with your mobile device</div>
              <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>or copy the test URL below</div>
            </div>
            <div className="flex items-center gap-2 p-2 mb-4 rounded text-sm font-mono" style={{ background: 'rgba(0,0,0,0.2)' }}>
              <input 
                type="text" 
                value={generateTestUrl(qrWidget)} 
                readOnly 
                className="flex-1 bg-transparent outline-none"
                style={{ color: 'var(--color-text-secondary)' }}
              />
              <button
                onClick={() => navigator.clipboard.writeText(generateTestUrl(qrWidget))}
                className="px-2 py-1 rounded hover:bg-gray-700/50"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Copy
              </button>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                Test URL is valid for 24 hours
              </div>
              <button 
                onClick={() => { setQrUrl(null); setQrWidget(null); }} 
                className="px-4 py-2 rounded bg-purple-600 hover:bg-purple-500 text-sm" 
                style={{ color: 'var(--color-text-primary)' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showEdit && (
        <div style={modalBackdropStyle} role="dialog" aria-modal="true">
          <div className="w-full max-w-2xl rounded-lg p-5" style={cardStyle}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Edit Widget Settings</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Theme</label>
                <select
                  value={editSettings?.theme || 'auto'}
                  onChange={e => setEditSettings(prev => ({ ...prev, theme: e.target.value as any }))}
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <option value="auto">Auto (System)</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Position</label>
                <select
                  value={editSettings?.position || 'bottom-right'}
                  onChange={e => setEditSettings(prev => ({ ...prev, position: e.target.value as any }))}
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                  <option value="top-right">Top Right</option>
                  <option value="top-left">Top Left</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Initial Message</label>
                <input
                  value={editSettings?.initialMessage || ''}
                  onChange={e => setEditSettings(prev => ({ ...prev, initialMessage: e.target.value }))}
                  placeholder="Hi! How can I help you today?"
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Button Text</label>
                <input
                  value={editSettings?.buttonText || ''}
                  onChange={e => setEditSettings(prev => ({ ...prev, buttonText: e.target.value }))}
                  placeholder="Chat with us"
                  className="w-full px-3 py-2 rounded"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Link Assistant
                  <span className="text-xs ml-1" style={{ color: 'var(--color-text-secondary)' }}>
                    (required)
                  </span>
                </label>
                <select
                  value={selectedAssistantId}
                  onChange={e => setSelectedAssistantId(e.target.value)}
                  className="w-full px-3 py-2 rounded mb-4"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                >
                  <option value="">Select an assistant...</option>
                  {assistants.map(assistant => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Custom CSS</label>
                <textarea
                  value={editSettings?.customCss || ''}
                  onChange={e => setEditSettings(prev => ({ ...prev, customCss: e.target.value }))}
                  placeholder="/* Add custom styles here */"
                  rows={4}
                  className="w-full px-3 py-2 rounded font-mono text-sm"
                  style={{ background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                <FiLink2 className="mr-2" />
                Currently linked to: <span className="font-medium ml-1" style={{ color: 'var(--color-text-primary)' }}>
                  {widgets.find(w => w.id === showEdit)?.linkedAssistant || 'No assistant'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowEdit(null)} 
                  className="px-4 py-2 rounded bg-gray-700/50 hover:bg-gray-700 text-sm"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => saveEdit(showEdit)} 
                  className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 text-sm"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Widgets;


